import React from "react";
import { ChevronRight } from "lucide-react";

/**
 * Mobile tones — rõ tương phản (icon đậm + viền rõ), tránh pastel nhạt giống nhau.
 * Light Mode: nền sáng, accent màu phân biệt từng module.
 */
export const MOBILE_TONES = {
  rose: {
    chip: "bg-white border-rose-200",
    icon: "bg-rose-600 text-white shadow-sm shadow-rose-200",
    text: "text-rose-800",
    value: "text-rose-700",
    accent: "bg-rose-600",
    softBg: "bg-rose-100",
    stripe: "border-l-rose-600",
    banner: "bg-rose-50 border-rose-200",
  },
  sky: {
    chip: "bg-white border-sky-200",
    icon: "bg-sky-600 text-white shadow-sm shadow-sky-200",
    text: "text-sky-800",
    value: "text-sky-700",
    accent: "bg-sky-600",
    softBg: "bg-sky-100",
    stripe: "border-l-sky-600",
    banner: "bg-sky-50 border-sky-200",
  },
  amber: {
    chip: "bg-white border-amber-300",
    icon: "bg-amber-500 text-white shadow-sm shadow-amber-200",
    text: "text-amber-900",
    value: "text-amber-700",
    accent: "bg-amber-500",
    softBg: "bg-amber-100",
    stripe: "border-l-amber-500",
    banner: "bg-amber-50 border-amber-300",
  },
  violet: {
    chip: "bg-white border-violet-200",
    icon: "bg-violet-600 text-white shadow-sm shadow-violet-200",
    text: "text-violet-900",
    value: "text-violet-700",
    accent: "bg-violet-600",
    softBg: "bg-violet-100",
    stripe: "border-l-violet-600",
    banner: "bg-violet-50 border-violet-200",
  },
  emerald: {
    chip: "bg-white border-emerald-200",
    icon: "bg-emerald-600 text-white shadow-sm shadow-emerald-200",
    text: "text-emerald-900",
    value: "text-emerald-700",
    accent: "bg-emerald-600",
    softBg: "bg-emerald-100",
    stripe: "border-l-emerald-600",
    banner: "bg-emerald-50 border-emerald-200",
  },
  teal: {
    chip: "bg-white border-teal-200",
    icon: "bg-teal-600 text-white shadow-sm shadow-teal-200",
    text: "text-teal-900",
    value: "text-teal-700",
    accent: "bg-teal-600",
    softBg: "bg-teal-100",
    stripe: "border-l-teal-600",
    banner: "bg-teal-50 border-teal-200",
  },
  indigo: {
    chip: "bg-white border-indigo-200",
    icon: "bg-indigo-600 text-white shadow-sm shadow-indigo-200",
    text: "text-indigo-900",
    value: "text-indigo-700",
    accent: "bg-indigo-600",
    softBg: "bg-indigo-100",
    stripe: "border-l-indigo-600",
    banner: "bg-indigo-50 border-indigo-200",
  },
  slate: {
    chip: "bg-white border-slate-200",
    icon: "bg-slate-700 text-white shadow-sm shadow-slate-200",
    text: "text-slate-800",
    value: "text-slate-900",
    accent: "bg-slate-600",
    softBg: "bg-slate-100",
    stripe: "border-l-slate-600",
    banner: "bg-slate-50 border-slate-200",
  },
};

export function getTone(name) {
  return MOBILE_TONES[name] || MOBILE_TONES.slate;
}

export const MODULE_TONES = {
  crm: "sky",
  hrm: "violet",
  elearning: "indigo",
  finance: "teal",
  reports: "amber",
  settings: "slate",
};

export function MobilePageShell({ children, className = "" }) {
  return (
    <div className={`min-h-full bg-[#EEF0F5] pb-6 ${className}`.trim()}>
      {children}
    </div>
  );
}

export function MobilePageHeader({
  title,
  subtitle,
  trailing,
  children,
  tone = "rose",
}) {
  const t = getTone(tone);
  return (
    <div className="relative overflow-hidden border-b border-gray-100 bg-white px-5 pb-5 pt-6">
      <div
        aria-hidden
        className={`pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full opacity-60 blur-2xl ${t.softBg}`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute -left-6 bottom-0 h-16 w-16 rounded-full opacity-50 blur-xl ${t.chip}`}
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold leading-snug text-gray-900">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 text-[12px] text-gray-500">{subtitle}</p>
          ) : null}
        </div>
        {trailing}
      </div>
      {children ? <div className="relative mt-4">{children}</div> : null}
    </div>
  );
}

export function MobileSectionHeader({ title, action, tone }) {
  const t = tone ? getTone(tone) : null;
  return (
    <div className="mb-2.5 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2.5">
        {t ? (
          <span className={`h-5 w-1.5 shrink-0 rounded-full ${t.accent}`} />
        ) : null}
        <h3 className="text-[14px] font-bold tracking-tight text-gray-900">
          {title}
        </h3>
      </div>
      {action}
    </div>
  );
}

/**
 * List row — soft colored icon chip when `tone` is set.
 */
export function MobileNavRow({
  icon: Icon,
  label,
  hint,
  onClick,
  danger = false,
  emphasize = false,
  tone,
}) {
  const t = tone ? getTone(tone) : null;
  let iconClass = "bg-gray-50 text-gray-500";
  if (danger) iconClass = "bg-rose-50 text-rose-600";
  else if (t) iconClass = t.icon;
  else if (emphasize) iconClass = "bg-rose-50 text-rose-700";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-gray-50/80 ${
        danger ? "text-rose-600" : "text-gray-800"
      }`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconClass}`}
      >
        {Icon ? <Icon size={18} strokeWidth={2} /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-sm font-medium ${
            danger ? "text-rose-600" : "text-gray-800"
          }`}
        >
          {label}
        </p>
        {hint ? (
          <p className="truncate text-[11px] text-gray-400">{hint}</p>
        ) : null}
      </div>
      <ChevronRight
        size={14}
        className={danger ? "text-rose-300" : "text-gray-300"}
      />
    </button>
  );
}

export function MobileNavGroup({ children, className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-gray-100/90 bg-white shadow-sm divide-y divide-gray-50 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

export function MobileStatChip({
  label,
  value,
  onClick,
  emphasize = false,
  tone = "sky",
  icon: Icon,
}) {
  const t = getTone(tone);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 flex-col rounded-2xl border border-l-[3px] px-3.5 py-3 text-left shadow-sm transition-colors active:brightness-95 ${t.chip} ${t.stripe}`}
    >
      <div className="flex items-center justify-between gap-2">
        {Icon ? (
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${t.icon}`}
          >
            <Icon size={16} strokeWidth={2.25} />
          </div>
        ) : (
          <span className={`h-2 w-2 rounded-full ${t.accent}`} />
        )}
        <ChevronRight size={14} className="text-gray-400" />
      </div>
      <p className={`mt-2.5 text-[11px] font-bold ${t.text}`}>{label}</p>
      <p
        className={`mt-0.5 text-xl font-black tabular-nums tracking-tight ${
          emphasize && Number(value) > 0 ? t.value : "text-gray-900"
        }`}
      >
        {value}
      </p>
    </button>
  );
}

/** KPI card — nền trắng + viền/accent màu đậm, dễ phân biệt trên mobile */
export function MobileStatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "sky",
  onClick,
}) {
  const t = getTone(tone);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[104px] flex-col rounded-2xl border border-l-[3px] bg-white p-3.5 text-left shadow-sm active:scale-[0.98] transition-transform ${t.stripe} border-gray-200/90`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${t.icon}`}
        >
          {Icon ? <Icon size={17} strokeWidth={2.25} /> : null}
        </div>
        <ChevronRight size={15} className="text-gray-400" />
      </div>
      <p className={`mt-2.5 text-[11px] font-bold leading-snug ${t.text}`}>
        {label}
      </p>
      <p className="mt-0.5 text-[22px] font-black tabular-nums tracking-tight text-gray-900">
        {value}
      </p>
      {hint ? (
        <p className="mt-1 line-clamp-1 text-[10px] font-semibold text-gray-500">
          {hint}
        </p>
      ) : (
        <span className="mt-1 block h-3.5" aria-hidden />
      )}
    </button>
  );
}

/** Feature shortcut card (2-col grid) — no KPI, just navigate */
export function MobileShortcutCard({
  label,
  hint,
  icon: Icon,
  tone = "slate",
  onClick,
}) {
  const t = getTone(tone);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[88px] flex-col rounded-2xl border border-l-[3px] border-gray-200 bg-white p-3.5 text-left shadow-sm active:scale-[0.98] transition-transform ${t.stripe}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${t.icon}`}
        >
          {Icon ? <Icon size={18} strokeWidth={2.25} /> : null}
        </div>
        <ChevronRight size={14} className="text-gray-400" />
      </div>
      <p className="mt-2.5 text-[13px] font-bold leading-snug text-gray-900">
        {label}
      </p>
      {hint ? (
        <p className="mt-0.5 line-clamp-1 text-[10px] font-medium text-gray-500">
          {hint}
        </p>
      ) : null}
    </button>
  );
}
